import {ListView} from '@github-ui/list-view'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Link as RouterLink} from '@github-ui/react-router'
import {PullsListItem} from '@github-ui/shared-dashboard-lists/pulls/PullsListItem'
import {testIdProps} from '@github-ui/test-id-props'
import {ArrowLeftIcon, StackIcon} from '@primer/octicons-react'
import {BranchName, VisuallyHidden} from '@primer/react'
import {useMemo} from 'react'

import {StackClosedPullWarning} from '../components/stacks/StackClosedPullWarning'
import {StackDetailActions} from '../components/stacks/StackDetailActions'
import {StackProgress} from '../components/stacks/StackProgress'
import styles from '../components/stacks/Stacks.module.css'
import {STACKS_CONTENT_TEST_ID} from '../components/stacks/test-ids'
import {repoPullsToListItems} from '../helpers/repo-pulls-dashboard-proto'
import {repoStackDetailRoute} from './repo-stack-detail-route'
import {stacksListPath} from './repo-stacks-list-route'

export function RepoStackDetail() {
  const {owner, repo} = useRouteParams(repoStackDetailRoute)
  const {data} = useSuspenseRouteQuery(repoStackDetailRoute, 'mainQuery')
  // The server orders pull requests from the top of the stack to its base. They can't be selected or reordered here.
  const pulls = useMemo(() => repoPullsToListItems(data.pullRequests), [data.pullRequests])
  const stack = data.stack
  if (!stack) return null

  return (
    <div {...testIdProps(STACKS_CONTENT_TEST_ID)}>
      <RouterLink to={stacksListPath({owner, repo})} className={styles.backLink}>
        <ArrowLeftIcon />
        Back to stacks
      </RouterLink>
      <div className={styles.detailHeader}>
        <div className={styles.detailHeading}>
          <h1 className={styles.detailTitle}>#{stack.number}</h1>
          <div className={styles.baseRow}>
            <VisuallyHidden>Base branch:</VisuallyHidden>
            <BranchName as="span">{stack.baseRefName}</BranchName>
            <ArrowLeftIcon aria-hidden="true" />
            <span className={styles.stackSize}>
              <StackIcon aria-hidden="true" />
              <span aria-hidden="true">{stack.size}</span>
              <VisuallyHidden>{stack.size} pull requests in this stack</VisuallyHidden>
            </span>
          </div>
          <StackClosedPullWarning closedPullRequest={stack.closedPullRequest} />
          <div className={styles.detailProgress}>
            <StackProgress mergedCount={stack.mergedCount} unmergedCount={stack.unmergedCount} />
          </div>
        </div>
        <StackDetailActions />
      </div>
      <div className={styles.listWrapper}>
        <ListView
          title={`Pull requests in stack #${stack.number}`}
          titleHeaderTag="h2"
          singularUnits="pull request"
          pluralUnits="pull requests"
          totalCount={pulls.length}
          as="ul"
          role="list"
        >
          {pulls.map(pull => (
            <PullsListItem key={String(pull.id)} pull={pull} showUnreadIndicator={false} />
          ))}
        </ListView>
      </div>
    </div>
  )
}
